import React, { useState, useRef, useEffect } from "react"
import { Button, Icon, Tooltip } from "@blueprintjs/core"
import css from "./ChatInputFiles.module.css"
import { useTranslation } from "react-i18next"

export type PastedFile = {
  file: File
  url: string
}

type ChatInputFilesProps = {
  pastedFiles: PastedFile[]
  handleRemoveImage: (index: number) => void
}

const ChatInputFiles: React.FC<ChatInputFilesProps> = ({
  pastedFiles,
  handleRemoveImage,
}) => {
  const { t } = useTranslation()
  const isImage = (file: File): boolean => {
    const acceptedImageTypes = [
      "image/gif",
      "image/jpg",
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/bmp",
      "image/tiff",
    ]
    return acceptedImageTypes.includes(file.type)
  }

  const isAudio = (file: File): boolean => {
    const acceptedAudioTypes = [
      "audio/mp3",
      "audio/mpeg",
      "audio/mp4",
      "audio/m4a",
      "audio/wav",
      "audio/webm",
      "audio/ogg",
      "audio/aac",
    ]
    return (
      acceptedAudioTypes.includes(file.type) ||
      !!file.name.match(/\.(mp3|mpeg|mp4|m4a|wav|webm|ogg|aac)$/i)
    )
  }

  const AudioPreview: React.FC<{ file: File; url: string }> = ({
    file,
    url,
  }) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const [duration, setDuration] = useState<number | null>(null)
    const [currentTime, setCurrentTime] = useState(0)
    const audioRef = useRef<HTMLAudioElement>(null)

    useEffect(() => {
      const audio = audioRef.current
      if (!audio) return

      const handleLoadedMetadata = () => {
        setDuration(audio.duration)
      }

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime)
      }

      const handleEnded = () => {
        setIsPlaying(false)
      }

      audio.addEventListener("loadedmetadata", handleLoadedMetadata)
      audio.addEventListener("timeupdate", handleTimeUpdate)
      audio.addEventListener("ended", handleEnded)

      return () => {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata)
        audio.removeEventListener("timeupdate", handleTimeUpdate)
        audio.removeEventListener("ended", handleEnded)
      }
    }, [])

    const togglePlayback = () => {
      const audio = audioRef.current
      if (!audio) return

      if (isPlaying) {
        audio.pause()
        setIsPlaying(false)
      } else {
        audio.play()
        setIsPlaying(true)
      }
    }

    const formatTime = (seconds: number) => {
      if (!seconds || isNaN(seconds)) return "0:00"
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, "0")}`
    }

    return (
      <div className={css.audioPreview}>
        <audio ref={audioRef} src={url} preload="metadata" />
        <div className={css.audioControls}>
          <Button
            icon={<Icon icon={isPlaying ? "pause" : "play"} size={16} />}
            minimal
            small
            onClick={togglePlayback}
          />
          <div className={css.audioInfo}>
            <div className={css.audioFileName}>
              {file.name.length > 20
                ? `${file.name.slice(0, 15)}...`
                : file.name}
            </div>
            <div className={css.audioTiming}>
              {formatTime(currentTime)} / {formatTime(duration || 0)}
            </div>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className={css.chatInputFilesContainer}>
      {pastedFiles.map((pastedFile, index) => {
        const file = pastedFile.file
        const fileType = file.name.substring(file.name.lastIndexOf(".") + 1)
        const isImageCheck = isImage(file)
        const isAudioCheck = isAudio(file)
        const previewStyle = isImageCheck
          ? { backgroundImage: `url(${pastedFile.url})` }
          : undefined
        return (
          <div key={index} className={css.fileContainer}>
            <div className={css.removeButtonContainer}>
              <Tooltip content={t("app.chatInput.removeFile")}>
                <Button
                  data-testid="remove-pasted-image-button"
                  className={css.removeButton}
                  icon={<Icon icon="cross-circle" size={19} color="black" />}
                  minimal
                  onClick={() => handleRemoveImage(index)}
                />
              </Tooltip>
            </div>

            <div
              className={css.filePreviewContainer}
              style={previewStyle}
              data-testid="file-preview-container"
            >
              {isAudioCheck ? (
                <AudioPreview file={file} url={pastedFile.url} />
              ) : !isImageCheck ? (
                <div className={css.filePreview}>
                  {fileType.toLowerCase() === "pdf" ? (
                    <img
                      src="/pdf.svg"
                      alt={t("app.chatInput.pdfIcon")}
                      width={28}
                      height={28}
                    />
                  ) : (
                    <Icon
                      icon="document"
                      size={28}
                      data-testid="document-preview-icon"
                    />
                  )}
                  <div className={css.filePreviewNameWrapper}>
                    <span className={css.fileName}>
                      {file.name.length > 20
                        ? `${file.name.slice(0, 15)}...`
                        : file.name}
                    </span>
                    <div className={css.fileType}>{fileType}</div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ChatInputFiles
